/**
 * Admin Storage Migrations (Sprint B — Master Plan v5.1)
 *
 * Mirrors storefront/lib/storage/migrations.ts:
 * - Migrate un-prefixed legacy keys to t:<slug>:<key>.
 * - Validate auth_token against backend before adopting per-tenant.
 *
 * Direct localStorage/sessionStorage access is allowed only here (and inside
 * StorageSlot). Everything else MUST go through the storage registry.
 */

import { STORAGE_KEYS, SESSION_KEYS, isBrowser } from './constants'

const TENANT_SCOPED_LOCAL_KEYS = [
  STORAGE_KEYS.BOUQUET_DRAFT,
  STORAGE_KEYS.BOUQUET_COMPOSITION_DRAFT,
  STORAGE_KEYS.GROUPED_NAV,
  STORAGE_KEYS.EXPANDED_GROUPS,
  STORAGE_KEYS.ACCORDION_STATE,
  STORAGE_KEYS.SAFE_SETTINGS_DRAFT,
  STORAGE_KEYS.ANALYTICS_DEVICE,
] as const

// Note: SESSION_KEYS.CHUNK_RELOAD is GLOBAL — not migrated.

/**
 * Migrate un-prefixed tenant-scoped keys to t:<slug>:<key>.
 * Caller must ensure window.__TENANT_SLUG__ is resolved first.
 */
export function migrateTenantScopedKeysToPrefixed(): void {
  if (!isBrowser) return

  const slug = typeof window !== 'undefined' ? window.__TENANT_SLUG__ : undefined
  if (!slug) return

  const migrate = (storage: Storage, key: string): void => {
    try {
      const prefixedKey = `t:${slug}:${key}`
      const legacyValue = storage.getItem(key)
      if (legacyValue === null) return

      if (storage.getItem(prefixedKey) === null) {
        storage.setItem(prefixedKey, legacyValue)
      }
      storage.removeItem(key)
    } catch (err) {
      console.warn(
        `[Admin Storage] failed to migrate ${key} for tenant ${slug}:`,
        err,
      )
    }
  }

  for (const key of TENANT_SCOPED_LOCAL_KEYS) {
    migrate(localStorage, key)
  }

  // Also handle the orphan 'token' key that one component (ActiveTab.tsx) used
  // — drop it; admin's auth flow uses STORAGE_KEYS.AUTH_TOKEN ('auth_token').
  try {
    localStorage.removeItem('token')
  } catch {
    // ignore
  }
}

/**
 * Sprint B.4: validate admin auth_token against backend before per-tenant migration.
 *
 * If backend confirms the token belongs to the current tenant: copy to
 * `t:<slug>:auth_token`, delete legacy.
 *
 * If backend rejects (401/403): delete legacy. Admin will re-login on this tenant.
 */
export async function migrateAdminAuthTokenForCurrentTenant(
  apiBase: string,
): Promise<{ migrated: boolean; tokenValid: boolean }> {
  if (!isBrowser) return { migrated: false, tokenValid: false }

  const slug = typeof window !== 'undefined' ? window.__TENANT_SLUG__ : undefined
  if (!slug) return { migrated: false, tokenValid: false }

  const prefixedKey = `t:${slug}:${STORAGE_KEYS.AUTH_TOKEN}`
  const legacyKey = STORAGE_KEYS.AUTH_TOKEN

  try {
    if (localStorage.getItem(prefixedKey) !== null) {
      // Already migrated for this tenant. Per Ralph review: do NOT cleanup
      // legacy here — without verifying prefixed is still valid we'd risk
      // logging the user out. apiClient's 401 unauthorized handler cleans
      // both atomically when needed.
      return { migrated: false, tokenValid: true }
    }

    const legacyToken = localStorage.getItem(legacyKey)
    if (!legacyToken) return { migrated: false, tokenValid: false }

    const url = apiBase
      ? `${apiBase.replace(/\/+$/, '')}/api/auth/me`
      : '/api/auth/me'

    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${legacyToken}` },
      credentials: 'include',
    })

    if (response.ok) {
      localStorage.setItem(prefixedKey, legacyToken)
      localStorage.removeItem(legacyKey)
      return { migrated: true, tokenValid: true }
    }

    localStorage.removeItem(legacyKey)
    return { migrated: false, tokenValid: false }
  } catch (err) {
    console.warn('[Admin Storage] auth token migration failed:', err)
    return { migrated: false, tokenValid: false }
  }
}

/**
 * Run post-bootstrap migrations.
 * Called from TenantContext after window.__TENANT_SLUG__ is set.
 */
export function runPostBootstrapMigrations(): void {
  if (!isBrowser) return
  migrateTenantScopedKeysToPrefixed()
}

/**
 * Clear all admin-related storage (debugging/testing).
 * Includes both un-prefixed and current-tenant prefixed forms.
 */
export function clearAllAdminStorage(): void {
  if (!isBrowser) return

  const slug = typeof window !== 'undefined' ? window.__TENANT_SLUG__ : undefined

  Object.values(STORAGE_KEYS).forEach(key => {
    try {
      localStorage.removeItem(key)
      if (slug) localStorage.removeItem(`t:${slug}:${key}`)
    } catch {
      // ignore
    }
  })

  Object.values(SESSION_KEYS).forEach(key => {
    try {
      sessionStorage.removeItem(key)
      if (slug) sessionStorage.removeItem(`t:${slug}:${key}`)
    } catch {
      // ignore
    }
  })
}
